/**
 * Module: screens/pantene/_pantene-atoms
 * Role: Shared sub-components for Pantene UGC screens (VideoUpload, ModerationStatus).
 *       CSS-in-JS atoms following the design token system.
 *
 * Uses: components/atoms, styles/tokens
 * Used by: screens/pantene/VideoUpload, screens/pantene/ModerationStatus
 */

import { CSSProperties } from 'react';
import { Ic } from '../../components/atoms';
import type { JobStage } from '../../api/pantene';
import { P, S, T } from '../../styles/tokens';

// ─── Spinner ────────────────────────────────────────────────────────────────
export function Spinner({ color = P.terracotta, size = 36 }: { color?: string; size?: number }) {
  const s: CSSProperties = {
    width: size,
    height: size,
    borderRadius: '50%',
    border: `3px solid ${P.line}`,
    borderTopColor: color,
    animation: 'spin 0.7s linear infinite',
    flexShrink: 0,
  };
  return <div style={s} />;
}

// ─── Stage labels & order ────────────────────────────────────────────────────
export const STAGE_LABELS: Record<JobStage, string> = {
  queued: 'В очереди',
  extracting: 'Кадры',
  transcribing: 'Речь',
  analyzing: 'Анализ',
  done: 'Готово',
  failed: 'Ошибка',
};

export const STAGE_ORDER: JobStage[] = [
  'queued',
  'extracting',
  'transcribing',
  'analyzing',
  'done',
];

export function stageIndex(stage: JobStage): number {
  const idx = STAGE_ORDER.indexOf(stage);
  return idx === -1 ? STAGE_ORDER.indexOf('analyzing') : idx;
}

// ─── Stage strip (moderation progress) ──────────────────────────────────────
export function StageStrip({ current }: { current: JobStage }) {
  const currentIdx = stageIndex(current);
  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 4,
      padding: '12px 16px 8px',
    }}>
      {STAGE_ORDER.map((stage, i) => {
        const isDone = i < currentIdx;
        const isActive = i === currentIdx;
        const dotBg = isDone ? P.success : isActive ? P.terracotta : P.line;

        return (
          <div key={stage} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
            <div style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: dotBg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.3s ease',
              flexShrink: 0,
            }}>
              {isDone
                ? <Ic name="check" size={14} color="#ffffff" />
                : isActive
                  ? <Spinner color="#ffffff" size={16} />
                  : <span style={{ font: T.label, color: S.tertiaryLabel }}>{i + 1}</span>
              }
            </div>
            <span style={{ font: T.label, color: isDone || isActive ? S.label : S.tertiaryLabel, textAlign: 'center' }}>
              {STAGE_LABELS[stage]}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Reasons list ─────────────────────────────────────────────────────────────
export function ReasonsList({ reasons, color }: { reasons: string[]; color: string }) {
  if (reasons.length === 0) return null;
  return (
    <div style={{
      width: '100%',
      background: '#ffffff',
      borderRadius: 14,
      border: `1px solid ${P.line}`,
      overflow: 'hidden',
    }}>
      {reasons.map((r, i) => (
        <div key={i} style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          padding: '11px 14px',
          borderBottom: i < reasons.length - 1 ? `1px solid ${P.line}` : 'none',
        }}>
          <div style={{ paddingTop: 2, flexShrink: 0 }}>
            <Ic name="xmark" size={14} color={color} />
          </div>
          <span style={{ font: T.subhead, color: S.label }}>{r}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Verdict icon circle ──────────────────────────────────────────────────────
export function VerdictIcon({
  name,
  color,
  bgAlpha = 0.12,
}: {
  name: 'check' | 'clock' | 'warning' | 'xmark';
  color: string;
  bgAlpha?: number;
}) {
  return (
    <div style={{
      width: 56,
      height: 56,
      borderRadius: '50%',
      background: `${color}${Math.round(bgAlpha * 255).toString(16).padStart(2, '0')}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <Ic name={name} size={26} color={color} />
    </div>
  );
}
